import { Box, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { getCountryCallingCode, getExampleNumber, parsePhoneNumber } from 'libphonenumber-js';
import { getCountries } from 'libphonenumber-js/core';
import examples from 'libphonenumber-js/examples.mobile.json';
import metadata from 'libphonenumber-js/metadata.min.json';
// eslint-disable-next-line no-use-before-define
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Value as E164Number } from 'react-phone-number-input';
import Input, { Country } from 'react-phone-number-input/input';
import countryNames from 'react-phone-number-input/locale/fr.json';
import { CustomCountrySelectWithFlags, PREFERRED_COUNTRIES } from './CustomCountrySelectWithFlags';

const DEFAULT_COUNTRY: Country = 'FR';

const getCountryFromValue = (phone?: E164Number | string): Country | undefined => {
  if (!phone) return undefined;
  try {
    const parsed = parsePhoneNumber(phone);
    return (parsed?.country as Country | undefined) || undefined;
  } catch {
    return undefined;
  }
};

interface MaterialPhoneInputProps {
  onChange?: (value?: E164Number) => void;
  value?: E164Number;
  placeholder?: string;
  onCountryChange?: (country?: Country) => void;
  disabled?: boolean;
  className?: string;
  error?: boolean | string;
  style?: any;
  name?: string;
  id?: string;
  allowedCountries?: Country[];
}

const useStyles = makeStyles(theme => ({
  phoneContainer: {
    position: 'relative',
    display: 'flex',
    height: '40px',
    alignItems: 'stretch',
    padding: '0 12px',
    borderRadius: 4,
    border: `1px solid ${theme.palette.text.secondary}`,
    transition: 'border 0.2s, padding 0.2s',
    gap: '8px',

    '&.focused': {
      border: `2px solid ${theme.palette.primary.main} !important`,
      padding: '0 11px',
    },
    '&.error': {
      borderColor: `${theme.palette.error.main} !important`,
    },
  },

  flagWrapper: {
    display: 'flex',
    alignItems: 'center',
  },

  countryCode: {
    display: 'flex',
    alignItems: 'center',
    color: 'rgba(0, 0, 0)',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    fontSize: '1rem',
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif !important',
  },

  inputWrapper: {
    display: 'flex',
    alignItems: 'center',
    flex: 1,

    '& .PhoneInputInput': {
      width: '100%',
      border: 'none',
      outline: 'none',
      backgroundColor: 'transparent',
      fontSize: '1rem',
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      color: theme.palette.text.primary,
      padding: 0,
      margin: 0,

      '&::placeholder': {
        color: theme.palette.text.secondary,
        opacity: 0.6,
      },

      '&:disabled': {
        color: theme.palette.text.disabled,
        cursor: 'not-allowed',
      },
    },
  },

  errorText: {
    marginTop: '4px',
  },
}));

const MaterialPhoneInput: React.FC<MaterialPhoneInputProps> = ({
  onChange,
  style,
  value,
  name = 'phone',
  id,
  placeholder = '',
  disabled = false,
  className = '',
  onCountryChange,
  error = false,
  allowedCountries,
  ...props
}) => {
  const classes = useStyles();
  const inputRef = useRef<HTMLInputElement>(null);
  const [country, setCountry] = useState<Country>(
    () => getCountryFromValue(value) ?? DEFAULT_COUNTRY
  );
  const [isFocused, setIsFocused] = useState(false);

  const countryOptions = useMemo(() => {
    const names = countryNames as Record<string, string>;
    const allCountries = getCountries(metadata) as Country[];
    const sourceCountries = allowedCountries?.length ? allowedCountries : allCountries;
    const allowedSet = new Set<string>(sourceCountries);
    const preferred = PREFERRED_COUNTRIES.filter(c => allowedSet.has(c));
    const others = sourceCountries.filter(c => !PREFERRED_COUNTRIES.includes(c));
    if (allowedCountries?.length) {
      others.sort((a, b) => (names[a] || a).localeCompare(names[b] || b, 'fr'));
    }
    const orderedCountries = [...preferred, ...others];

    if (country && !allowedSet.has(country)) {
      orderedCountries.push(country);
    }

    return orderedCountries.map(c => ({
      value: c,
      label: names[c] || c,
    }));
  }, [allowedCountries, country]);

  // Sync flag/country from an externally loaded number (e.g. profile edit).
  // Skip while focused so typing / manual country picks are not overwritten mid-edit.
  useEffect(() => {
    if (isFocused) return;
    const parsedCountry = getCountryFromValue(value);
    if (parsedCountry) {
      setCountry(prev => (prev === parsedCountry ? prev : parsedCountry));
    }
  }, [value, isFocused]);

  const handleFocusInput = () => {
    inputRef.current?.focus();
  };

  const dynamicPlaceholder = useMemo(() => {
    if (country) {
      const example = getExampleNumber(country, examples);
      if (example) {
        return example.formatNational();
      }
    }
    return placeholder;
  }, [country, placeholder]);

  const handleCountryChange = (newCountry?: Country) => {
    if (newCountry) {
      setCountry(newCountry);
      if (onCountryChange) {
        onCountryChange(newCountry);
      }
    }
  };
  const internalOnChange = (val?: E164Number) => {
    if (onChange) {
      onChange(!val ? '' : (val as any));
    }
  };

  return (
    <>
      <Box
        style={style}
        className={`${classes.phoneContainer} ${className} ${error ? 'error' : ''} ${
          isFocused ? 'focused' : ''
        }`}>
        <div className={classes.flagWrapper}>
          <CustomCountrySelectWithFlags
            value={country}
            onChange={handleCountryChange}
            options={countryOptions}
          />
        </div>

        <span className={classes.countryCode} onClick={handleFocusInput}>
          +{getCountryCallingCode(country)}
        </span>

        <div className={classes.inputWrapper}>
          <Input
            ref={inputRef}
            id={id}
            className="PhoneInputInput"
            country={country}
            value={value}
            name={name}
            onChange={internalOnChange as any}
            placeholder={dynamicPlaceholder}
            disabled={disabled}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            {...props}
          />
        </div>
      </Box>

      {error && typeof error === 'string' && (
        <Typography color="error" variant="caption" className={classes.errorText}>
          {error}
        </Typography>
      )}
    </>
  );
};

export default MaterialPhoneInput;
